import { api, type AttentionReadModel, type Dashboard } from './api';
import { failedAttentionSlice, mergeAttentionModel, type AttentionSlice, type AttentionSliceName } from './attention-merge';
import { cacheGeneration, cachedGet, invalidateCache, onQueryCacheScopeChange, peekCache, writeCache } from './query-cache';

export const QK = {
  dashboard: 'get:/dashboard',
  attention: 'get:/attention',
  rentDay: 'get:/rent-day',
  rentOverdue: 'get:/rent-overdue',
  urgentRent: 'get:/business/urgent-rent',
  utilitiesUnpaid: 'get:/business/utilities-unpaid-previous',
  approvals: 'get:/control-plane/approvals?status=pending',
  olegSignals: 'get:/oleg/signals',
  leadsWorkspace: 'get:/leads/workspace',
  businessOverview: 'get:/business/overview',
  businessLinks: 'get:/business-links',
  moneyRoadmap: (month: string) => `get:/business/financial-roadmap?month=${month}`,
  moneyObligations: (asOf: string) => `get:/business/fin-roadmap?view=money&as_of=${asOf}`,
  bookings: (query: string) => `get:/business/bookings?${query}`,
  rentCalendar: (from: string, to: string) => `get:/rent-calendar?from=${from}&to=${to}`,
  events: (from: string, to: string) => `get:/events?from=${from}&to=${to}`,
} as const;

const ATTENTION_TIMEOUT_MS = 10_000;

const ATTENTION_SLICES: Array<{ name: AttentionSliceName; path: string }> = [
  { name: 'control_plane', path: '/attention/control-plane' },
  { name: 'money', path: '/attention/money' },
  { name: 'booking_risk', path: '/attention/booking-risk' },
];

type AttentionListener = { generation: number; notify: (model: AttentionReadModel) => void };
const attentionListeners = new Set<AttentionListener>();
let attentionGeneration = 0;

onQueryCacheScopeChange(() => {
  attentionGeneration += 1;
});

export function subscribeAttention(listener: (model: AttentionReadModel) => void): () => void {
  const entry = { generation: cacheGeneration(), notify: listener };
  attentionListeners.add(entry);
  return () => attentionListeners.delete(entry);
}

function publishAttention(model: AttentionReadModel, atGeneration: number): void {
  if (atGeneration !== cacheGeneration()) return;
  writeCache(QK.attention, model, true, atGeneration);
  for (const listener of attentionListeners) {
    if (listener.generation === atGeneration) listener.notify(model);
  }
}

function sliceIsPending(model: AttentionReadModel, name: AttentionSliceName): boolean {
  const sources = model.sources;
  if (!sources) return false;
  const keys = name === 'control_plane'
    ? (['jack_inbox', 'guest_inbox', 'control_plane_runs'] as const)
    : name === 'money' ? (['money'] as const) : (['oleg'] as const);
  return keys.some((key) => sources[key]?.status === 'pending');
}

async function hydrateAttentionSlices(base: AttentionReadModel, attentionToken: number, atGeneration: number): Promise<void> {
  const due = ATTENTION_SLICES.filter((slice) => sliceIsPending(base, slice.name));
  if (!due.length) return;
  const pending = due.map(async (slice) => {
    try {
      return await api.get<AttentionSlice>(slice.path);
    } catch {
      return failedAttentionSlice(slice.name);
    }
  });
  let current = base;
  for (const job of pending) {
    const slice = await job;
    if (atGeneration !== cacheGeneration() || attentionToken !== attentionGeneration) return;
    current = mergeAttentionModel(current, slice);
    publishAttention(current, atGeneration);
  }
}

/** Одна сводка на Сегодня, Входящие и бейджи меню. Первая отрисовка — быстрые местные данные, обрыв 10 с. Медленные срезы дорисовываются отдельно. */
export function fetchAttentionModel(options?: { force?: boolean }): Promise<AttentionReadModel> {
  return cachedGet(QK.attention, async () => {
    const atGeneration = cacheGeneration();
    const attentionToken = ++attentionGeneration;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Сводка недоступна'));
      }, ATTENTION_TIMEOUT_MS);
    });
    try {
      const fast = await Promise.race([
        api.get<AttentionReadModel>('/attention', { signal: controller.signal }),
        timeout,
      ]);
      if (atGeneration === cacheGeneration()) {
        queueMicrotask(() => { void hydrateAttentionSlices(fast, attentionToken, atGeneration); });
      }
      return fast;
    } finally {
      clearTimeout(timer);
    }
  }, options);
}

export function fetchDashboard(options?: { force?: boolean }): Promise<Dashboard> {
  return cachedGet(QK.dashboard, () => api.get<Dashboard>('/dashboard'), options);
}

export function peekAttention(): AttentionReadModel | undefined {
  return peekCache<AttentionReadModel>(QK.attention);
}

export function peekDashboard(): Dashboard | undefined {
  return peekCache<Dashboard>(QK.dashboard);
}

export function invalidateOwnerAttention(): void {
  invalidateCache(QK.attention);
  invalidateCache(QK.dashboard);
}
